import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { mediaUrl } from "../lib/api";

const fmt = (n) => `$${n.toFixed(2)}`;

export default function CartDrawer() {
  const {
    items,
    isOpen,
    setIsOpen,
    updateQty,
    removeItem,
    subtotal,
    clear,
    count,
  } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    setIsOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col p-0 gap-0">
        <SheetHeader className="px-5 py-4 border-b border-[#eaeaea]">
          <SheetTitle className="font-serif-display text-[#333] text-lg flex items-center gap-2">
            <ShoppingBag size={20} className="text-[#0f9fbc]" /> Your Cart ({count})
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
            <ShoppingBag size={48} className="text-[#d7e6ea]" />
            <p className="font-serif-body text-[#666] mt-4">
              Your cart is empty.
            </p>
            <button
              onClick={() => setIsOpen(false)}
              className="mt-5 bg-[#0f9fbc] hover:bg-[#008da9] text-white font-serif-body px-6 py-2.5 rounded-[4px] transition-colors"
            >
              Continue Shopping
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {items.map((item) => (
                <div key={item.id} className="flex gap-3">
                  <img
                    src={mediaUrl(item.image)}
                    alt={item.name}
                    className="w-20 h-20 rounded-md object-cover border border-[#eaeaea] shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-serif-body text-[#333] text-[14px] leading-snug line-clamp-2">
                      {item.name}
                    </p>
                    <p className="font-serif-body text-[#0f9fbc] text-[14px] font-bold mt-1">
                      {fmt(item.price)}
                    </p>
                    <div className="flex items-center gap-3 mt-2">
                      <div className="flex items-center border border-[#eaeaea] rounded-md">
                        <button
                          onClick={() => updateQty(item.id, item.qty - 1)}
                          className="px-2 py-1 text-[#666] hover:text-[#0f9fbc]"
                          aria-label="Decrease quantity"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="px-3 font-serif-body text-[14px] text-[#333] min-w-[28px] text-center">
                          {item.qty}
                        </span>
                        <button
                          onClick={() => updateQty(item.id, item.qty + 1)}
                          className="px-2 py-1 text-[#666] hover:text-[#0f9fbc]"
                          aria-label="Increase quantity"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="text-[#b0b0b0] hover:text-[#d9534f] transition-colors"
                        aria-label="Remove item"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="font-serif-body text-[#333] text-[14px] font-bold whitespace-nowrap">
                    {fmt(item.price * item.qty)}
                  </div>
                </div>
              ))}

              <button
                onClick={clear}
                className="font-serif-body text-[13px] text-[#999] hover:text-[#d9534f] transition-colors"
              >
                Clear cart
              </button>
            </div>

            <div className="border-t border-[#eaeaea] px-5 py-5">
              <div className="flex items-center justify-between mb-4">
                <span className="font-serif-body text-[#333] text-[16px]">
                  Subtotal
                </span>
                <span className="font-serif-display text-[#0f9fbc] text-xl font-bold">
                  {fmt(subtotal)}
                </span>
              </div>
              <p className="font-serif-body text-[12px] text-[#999] mb-4">
                Shipping &amp; taxes calculated at checkout.
              </p>
              <button
                onClick={handleCheckout}
                className="w-full bg-[#0f9fbc] hover:bg-[#008da9] text-white font-serif-body font-bold py-3 rounded-[4px] transition-colors"
              >
                Proceed to Checkout
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
